/**
 * ServicesModal.tsx — running docker-container management.
 *
 * Lists running containers (GET /api/services) with a per-row Stop action;
 * the footer carries Stop all + Close. Two scopes: with `projectId` (opened
 * from the MenuBar's right action slot / the StatusBar's docker button inside
 * a project) the list and Stop all narrow to that project's task containers
 * via `?project=`; without it (the start page's top-right services button)
 * every app-managed container is shown. Per-row Stop works the same in both.
 *
 * StopServicesDialog (same file) is the close-project variant: asks whether
 * to stop the project's services or leave them running, with a "remember
 * choice" checkbox whose value lands in Settings → Engine ("Stop services on
 * close").
 */
import { useEffect, useState } from 'react';
import { Stop } from '@phosphor-icons/react';

import { Modal } from './Modal';

export interface DockerService {
  id: string;
  name: string;
  image: string;
  status: string;
  project: string;
}

interface ServicesModalProps {
  backendOrigin: string | null;
  token?: string | null;
  onClose: () => void;
  /** Scope the list + Stop all to one project's task containers (the
   * in-project view). Omit for the all-projects view (the start page). */
  projectId?: string | null;
}

function authHeaders(token?: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function errorText(r: Response): Promise<string> {
  try {
    const j = (await r.json()) as { detail?: unknown };
    if (typeof j?.detail === 'string') return j.detail;
  } catch { /* ignore */ }
  return `request failed (${r.status})`;
}

export function ServicesModal({ backendOrigin, token, onClose, projectId }: ServicesModalProps): JSX.Element | null {
  const [services, setServices] = useState<DockerService[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stopping, setStopping] = useState<string | null>(null); // container name
  const [stoppingAll, setStoppingAll] = useState(false);

  const listUrl = projectId
    ? `${backendOrigin}/api/services?project=${encodeURIComponent(projectId)}`
    : `${backendOrigin}/api/services`;

  const reload = async (): Promise<void> => {
    if (!backendOrigin) return;
    setError(null);
    try {
      const res = await fetch(listUrl, {
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(await errorText(res));
      const j = (await res.json()) as { services: DockerService[] };
      setServices(j.services);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendOrigin, projectId]);

  if (!backendOrigin) return null;

  const stopOne = async (name: string): Promise<void> => {
    if (stopping || stoppingAll) return;
    setStopping(name);
    try {
      const res = await fetch(`${backendOrigin}/api/services/${encodeURIComponent(name)}/stop`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(await errorText(res));
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setStopping(null);
    }
  };

  const stopAll = async (): Promise<void> => {
    if (stopping || stoppingAll) return;
    setStoppingAll(true);
    try {
      // Scoped: only this project's task containers. Unscoped: the backend's
      // stop-all, which itself only touches app-managed containers.
      const url = projectId
        ? `${backendOrigin}/api/projects/${encodeURIComponent(projectId)}/services/stop`
        : `${backendOrigin}/api/services/stop-all`;
      const res = await fetch(url, {
        method: 'POST',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(await errorText(res));
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setStoppingAll(false);
    }
  };

  return (
    <Modal title={projectId ? 'Project services' : 'Services'} onClose={onClose}>
      <div className="services-list">
        {error && <div className="settings-conn-status err">{error}</div>}
        {services === null && !error && <div className="param-hint">Loading…</div>}
        {services !== null && services.length === 0 && (
          <div className="param-hint">
            {projectId ? 'No running services for this project.' : 'No running docker containers.'}
          </div>
        )}
        {(services ?? []).map((s) => (
          <div key={s.id} className="services-row">
            <div className="services-row-main">
              <div className="services-row-name" title={s.image}>
                {s.name}
                {!projectId && s.project && <span className="agents-fence read-only">{s.project}</span>}
              </div>
              <div className="services-row-meta">
                <code>{s.image}</code> · {s.status}
              </div>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              disabled={stopping === s.name || stoppingAll}
              onClick={() => void stopOne(s.name)}
            >
              {stopping === s.name ? 'Stopping…' : <><Stop size={12} aria-hidden /> Stop</>}
            </button>
          </div>
        ))}
      </div>
      <div className="modal-footer-row">
        <span className="param-hint">
          {(services ?? []).length} running container{(services ?? []).length === 1 ? '' : 's'}
          {projectId ? ' · this project' : ''}
        </span>
        <span className="flex-spacer" />
        <button
          type="button"
          className="btn btn-danger"
          disabled={stoppingAll || (services ?? []).length === 0}
          onClick={() => void stopAll()}
        >
          {stoppingAll ? 'Stopping all…' : 'Stop all'}
        </button>
        <button type="button" className="btn" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}

/** The close-project prompt: stop the project's docker services or leave
 *  them running, optionally remembering the choice. */
export function StopServicesDialog({
  backendOrigin,
  token,
  projectId,
  onChoice,
}: {
  backendOrigin: string | null;
  token?: string | null;
  projectId: string | null;
  /** (stop, remember) — the caller persists the remembered value and closes
   *  the project; the stop call itself is fire-and-forget best-effort here. */
  onChoice: (stop: boolean, remember: boolean) => void;
}): JSX.Element {
  const [remember, setRemember] = useState(false);
  const [checking, setChecking] = useState(true);
  const [hasServices, setHasServices] = useState(false);

  // Only ask when the project actually has running services — otherwise the
  // prompt is noise; close proceeds as "leave running".
  useEffect(() => {
    if (!backendOrigin || !projectId) {
      setChecking(false);
      setHasServices(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${backendOrigin}/api/services?project=${encodeURIComponent(projectId)}`,
          { headers: authHeaders(token) },
        );
        if (!res.ok) throw new Error(await errorText(res));
        const j = (await res.json()) as { services: DockerService[] };
        if (!cancelled) setHasServices(j.services.length > 0);
      } catch {
        if (!cancelled) setHasServices(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [backendOrigin, projectId, token]);

  // No services (or the check failed) — nothing to ask; skip straight to
  // "leave running" without persisting anything.
  useEffect(() => {
    if (!checking && !hasServices) onChoice(false, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checking, hasServices]);

  const stopNow = (stop: boolean) => {
    if (stop && backendOrigin && projectId) {
      // Fire-and-forget best-effort stop of the project's containers.
      void fetch(`${backendOrigin}/api/projects/${encodeURIComponent(projectId)}/services/stop`, {
        method: 'POST',
        headers: authHeaders(token),
      }).catch(() => { /* closing anyway */ });
    }
    onChoice(stop, remember);
  };

  return (
    <Modal title="Stop services?" onClose={() => stopNow(false)}>
      <div className="services-stop-dialog">
        <p>
          This project has running docker services. Stop them before closing,
          or leave them running in the background?
        </p>
        <label className="trigger-armed-toggle">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
          Remember choice
        </label>
        <div className="modal-footer-row">
          <span className="flex-spacer" />
          <button type="button" className="btn" onClick={() => stopNow(false)}>
            Leave running
          </button>
          <button type="button" className="btn btn-danger" onClick={() => stopNow(true)}>
            Stop services
          </button>
        </div>
      </div>
    </Modal>
  );
}
